import { getApiBaseUrl } from '../utils/environment.mjs';
import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from "react";
import useExpenseForm from "../expenseReimbursement/hook/useExpenseReimbursement.mjs";
import ReactDOM from 'react-dom';
import usePdfViewer from "../expenseReimbursement/hook/usePdfViewer.mjs";
import InvoiceService from "../expenseReimbursement/service/invoiceUploadService.mjs";
import { useParams } from "react-router-dom";
import { ClaimStatusBanner } from "./claimStatusBanner.mjs";
import { AdminFeedbackSection } from "./adminFeedbackBanner.mjs";
import { AuthContext } from "../authProvider.mjs";
import axios from "axios";
import CurrencyDropdown from "./currencyDropdown.mjs";
import { getCurrencyCodeForCountry, isValidCurrency } from "./getCurrencyCode.mjs";
import seedlogo from "../assets/seedlogo.jpg";
import InfectedFileNotice from "./InfectedFileNotice.mjs";
import { isInfected, REVIEWER_INFECTED_MESSAGE } from "../utils/scanStatus.mjs";
import InvoiceUploader from "./invoiceUploader.mjs";
import DateInput from './DateInput.mjs';
import ClaimPtaeAllocation from './ClaimPtaeAllocation.mjs';
import ClaimHistoryPanel from './ClaimHistoryPanel.mjs';
import { exportExpenseClaimToExcel } from '../utils/claimExcel.mjs';
import { useDialog } from '../components/dialog/DialogProvider.mjs';
import PageShell from './PageShell.mjs';
import { parseLocalDate, formatLocalDate } from '../utils/dateOnly.mjs';
import {
  needsPurpose, purposeCaretIndex, linesNeedingPurpose,
  isUntouchedTemplate, DESCRIPTION_TEMPLATE, missingStructureLabels, withStructure,
} from '../utils/expensePurpose.mjs';


// -- Reading invoices into their rows ----------------------------------------
// Only claims whose OWNER is in a region with extraction switched on get this;
// the server decides and says so in each upload response (extractionEnabled).
// One module-level service instance, like the hook's, so the callbacks below
// don't depend on an object that is rebuilt on every render.
const extractionService = new InvoiceService();

// At most this many invoices are read at once from one form, however many
// batches or single attaches are waiting. Each read is a Document AI call.
const EXTRACTION_CONCURRENCY = 4;

const isBlankValue = (v) => v === undefined || v === null || String(v).trim() === '';
// New rows start with an amount of 0 or '', so 0 counts as "not filled in".
const isBlankAmount = (v) => isBlankValue(v) || Number(v) === 0;

// DD-MM-YYYY of a row's LOCAL calendar day - the form the server uses for a
// date-based receipt number and the description's "when". '' when there is none.
const dayMonthYear = (value) => {
  const ymd = formatLocalDate(value);
  return ymd ? ymd.split('-').reverse().join('-') : '';
};

// The server's descriptionPrefill shape, rebuilt here so "when" can follow the
// row's own date. A missing slot keeps its label; nothing when both are missing.
const descriptionStub = (who, when) => (
  who || when ? `who: ${who}, what/why: , when: ${when}`.trimEnd() : ''
);

/**
 * Merge one extraction result into its row, filling ONLY the fields that are
 * still empty. Whatever the user has typed wins, including anything typed while
 * the invoice was being read.
 *
 * Sets `extractionStatus` for the row's note: 'done' when something was filled,
 * 'failed' when the read failed or found nothing. When fields were found but
 * the user had already filled every one of them, the note is dropped - saying
 * "filled from the invoice" there would be untrue.
 */
const mergeExtractionIntoRow = (row, result) => {
  const { extractionStatus: _previous, ...base } = row;
  // Switched off between the upload and the read: behave as if never asked.
  if (result && result.status === 'disabled') return base;
  if (!result || result.status !== 'ok') return { ...base, extractionStatus: 'failed' };

  const fields = result.fields || {};
  const next = { ...base };
  let found = 0;
  let filled = 0;
  const fill = (key, value, isBlank = isBlankValue) => {
    if (isBlankValue(value)) return false;
    found += 1;
    if (!isBlank(base[key])) return false;
    next[key] = value;
    filled += 1;
    return true;
  };

  // A calendar day, built as LOCAL midnight like DateInput's own values.
  // new Date('YYYY-MM-DD') is UTC midnight - the previous day west of UTC.
  // Only the YYYY-MM-DD the server sends; anything else would go through the
  // browser's loose Date parser.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(fields.expenditureDate || ''))
    ? parseLocalDate(fields.expenditureDate)
    : null;
  // An untouched "today" from + Add Expense is a placeholder, not a user
  // value, so the receipt's own date replaces it.
  const invoiceHasDate = !!date && !Number.isNaN(date.getTime());
  const filledDate = invoiceHasDate
    && fill('expenditureDate', date, (v) => base.expenditureDateIsDefault === true || isBlankValue(v));
  if (filledDate) delete next.expenditureDateIsDefault;

  // The row's date once merged: the user's own if they typed one, else the
  // receipt's. An untouched "today" placeholder is not a date anyone gave.
  const finalDay = next.expenditureDateIsDefault === true ? '' : dayMonthYear(next.expenditureDate);

  // Only a number printed on the document fills the box. With none, it stays
  // blank for the claimant to type - the date is no longer used in its place
  // (and an older server still sending a date stand-in is ignored here).
  if (result.receiptNumberSource !== 'date' && typeof fields.receiptNumber === 'string') {
    fill('receiptNumber', fields.receiptNumber.trim());
  }

  // The location box is free text with a city search, so a plain city name
  // displays and saves as it is.
  if (typeof fields.expenditureLocation === 'string') {
    fill('expenditureLocation', fields.expenditureLocation.trim());
  }

  // Only a code the currency picker offers. Anything else would sit in the
  // box as a value the user cannot pick, and fail the rate lookup.
  const currency = typeof fields.expenditureCurrency === 'string'
    ? fields.expenditureCurrency.trim().toUpperCase()
    : '';
  const filledCurrency = currency && isValidCurrency(currency)
    ? fill('expenditureCurrency', currency)
    : false;

  // A rate only means something for a date AND a currency. If either was empty
  // until now, whatever rate the row holds was never looked up for them (a
  // USD row reloaded from a draft holds 1), so clear it: the hook's auto-fetch
  // prices a row whose rate is 0 once date, currency and amount are all set.
  // USD alone is 1 by definition and needs no lookup.
  if (filledDate || filledCurrency) {
    const isUsd = String(next.expenditureCurrency || '').trim().toUpperCase() === 'USD';
    next.exchangeRate = isUsd ? 1 : 0;
    next.usdEquivalent = isUsd ? Math.round((parseFloat(next.receiptAmount) || 0) * 100) / 100 : 0;
  }

  const amount = Number(fields.receiptAmount);
  if (fields.receiptAmount != null && Number.isFinite(amount) && amount > 0
      && fill('receiptAmount', amount, isBlankAmount)) {
    // Keep USD in step if the row still has a rate it can use.
    const rate = parseFloat(next.exchangeRate);
    if (rate > 0) next.usdEquivalent = Math.round((amount / rate) * 100) / 100;
  }

  // "who: <supplier>, what/why: , when: <date>" - the purpose is always left
  // for the user, and submitting is blocked until they write it. Built here
  // rather than taken from descriptionPrefill, whose "when" is the extracted
  // date: the row's final date is the one the claim will carry. Only when the
  // invoice gave a supplier or a date, as on the server - a stub made purely
  // of the user's own date would be reported as "filled from the invoice".
  const supplier = typeof fields.supplierName === 'string' ? fields.supplierName.trim() : '';
  if (supplier || invoiceHasDate) {
    // A new line's untouched who / what/why / when template counts as blank.
    fill('expenditureDescription', descriptionStub(supplier, finalDay),
      (v) => isBlankValue(v) || isUntouchedTemplate(v));
  }

  if (filled > 0) next.extractionStatus = 'done';
  else if (found === 0) next.extractionStatus = 'failed';
  return next;
};

// Invoices can be PDF, JPEG or PNG. Downloads keep each file's real type and
// extension: forcing every one into an application/pdf blob named ".pdf" left
// photos unopenable.
const EXTENSION_BY_TYPE = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
};
const TYPE_BY_EXTENSION = { pdf: 'application/pdf', jpg: 'image/jpeg', png: 'image/png' };

/** pdf | jpg | png from the first recognised content type, then the URL's path. */
const documentExtension = (url, ...contentTypes) => {
  for (const type of contentTypes) {
    const ext = EXTENSION_BY_TYPE[String(type || '').split(';')[0].trim().toLowerCase()];
    if (ext) return ext;
  }
  try {
    const match = /\.(pdf|jpe?g|png)$/i.exec(new URL(url).pathname);
    if (match) return match[1].toLowerCase() === 'jpeg' ? 'jpg' : match[1].toLowerCase();
  } catch (_e) { /* not an absolute URL - fall through */ }
  // Documents stored before images were accepted are PDFs.
  return 'pdf';
};


// Enhanced PDF Renderer Component with full viewing capabilities
const PDFRenderer = ({ pdfUrl, itemId, index, expenditureDescription, infected = false, noticeAudience = 'claimant' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);

  const handleIframeLoad = () => {

    setLoading(false);
    setError(null);
  };

  const handleIframeError = () => {
    console.error(`PDF iframe failed to load for item ${index + 1}`);
    setError('Failed to load document');
    setLoading(false);
  };

  const retryLoad = () => {
    setRetryCount(prev => prev + 1);
    setLoading(true);
    setError(null);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // The virus scan removed this file: no frame, no Open link, just the reason.
  // Kept as a card in the same slot so the receipt numbering below stays put.
  if (infected) {
    return (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-4 sm:mb-6" data-print-exclude="true">
        <div className="bg-gray-800 text-white px-4 py-3">
          <span className="text-sm line-clamp-2 sm:line-clamp-none">
            Expense Item {index + 1} - Receipt Document
            {expenditureDescription && ` - ${expenditureDescription}`}
          </span>
        </div>
        <div className="p-4">
          <InfectedFileNotice audience={noticeAudience} className="text-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden mb-4 sm:mb-6 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`} data-print-exclude="true">
      {/* Phones: title (clamped to two lines - the full description is in the
          line item above) over a full-width button row. sm+: original row. */}
      <div className="bg-gray-800 text-white px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm line-clamp-2 sm:line-clamp-none">
            Expense Item {index + 1} - Receipt Document
            {expenditureDescription && ` - ${expenditureDescription}`}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Android Chrome cannot show a PDF inside a frame, so phones get a
              direct way to open the receipt. */}
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="sm:hidden flex-1 min-h-[44px] inline-flex items-center justify-center px-3 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
          >
            Open
          </a>
          <button
            onClick={toggleFullscreen}
            className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
          >
            {isFullscreen ? 'Normal View' : 'Fullscreen'}
          </button>
          {error && (
            <button
              onClick={retryLoad}
              className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-3 py-1 text-sm bg-stanford-red hover:bg-stanford-light-red rounded transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      </div>

      {/* Screen version - fully interactive PDF viewer */}
      <div className="screen-only">
        {loading && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-2"></div>
            <p className="text-gray-600">Loading document...</p>
          </div>
        )}

        {error && (
          <div className="p-4 text-center text-red-600">
            <p>{error}</p>
            <p className="text-sm text-gray-500 mt-2">Please try refreshing or contact support if the issue persists.</p>
          </div>
        )}

        <div className="w-full">
          <iframe
            ref={iframeRef}
            key={`${itemId}-${retryCount}`}
            src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH&zoom=page-width`}
            className="w-full h-[480px] sm:h-[600px] border-0"
            title={`Receipt for expense item ${index + 1}`}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            style={{ display: loading || error ? 'none' : 'block' }}
            scrolling="auto"
            frameBorder="0"
            allowFullScreen
          />
        </div>
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="bg-gray-800 text-white px-4 py-3 flex items-center justify-between gap-3">
            <span className="min-w-0">Expense Item {index + 1} - Receipt Document</span>
            <button
              onClick={toggleFullscreen}
              className="shrink-0 min-h-[44px] sm:min-h-0 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              Close
            </button>
          </div>
          <iframe
            src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH&zoom=page-width`}
            className="flex-1 w-full border-0"
            title={`Fullscreen - Receipt for expense item ${index + 1}`}
            scrolling="auto"
            frameBorder="0"
          />
        </div>
      )}
    </div>
  );
};

// Admin PDF Download Modal Component
const AdminPdfDownloadModal = ({ isOpen, onClose, expenseItems }) => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { alert } = useDialog();
  const [downloadingPdfs, setDownloadingPdfs] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const downloadAllPdfs = async () => {
    setDownloadingPdfs(true);
    setDownloadProgress(0);

    const itemsWithPdfs = expenseItems.filter(item =>
      item.invoicePdfGoogleCloudUrl || item.pdfUrl
    );

    for (let i = 0; i < itemsWithPdfs.length; i++) {
      const item = itemsWithPdfs[i];
      // Removed by the virus scan: nothing to fetch. It stays in the list (and
      // keeps its number) so the reviewer sees why that receipt is missing.
      if (isInfected(item)) continue;
      try {

        // Fetch PDF directly from signed GCS URL
        const signedPdfUrl = item.pdfUrl || item.invoicePdfGoogleCloudUrl;
        if (!signedPdfUrl || !signedPdfUrl.startsWith('http')) continue;

        const response = await axios({
          method: 'GET',
          url: signedPdfUrl,
          responseType: 'blob',
        });

        // Use item data for metadata (headers not available from GCS signed URLs)
        const receiptNumber = item.receiptNumber;
        const invoiceId = item.invoiceId;


        // Create download link. The type and extension follow the file itself
        // (what GCS served, then what the upload recorded, then the stored
        // name), so a JPEG receipt downloads as a .jpg that opens.
        const extension = documentExtension(
          signedPdfUrl,
          response.data?.type,
          response.headers?.['content-type'],
          item.contentType
        );
        const blob = new Blob([response.data], { type: TYPE_BY_EXTENSION[extension] });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Generate filename using data from headers first, then fallback to item data
        // Sanitize invoice number to prevent XSS and path traversal in filename
        const rawInvoiceNumber = receiptNumber || invoiceId || item.invoiceId || item.receiptNumber || `item-${item.id}`;
        const safeInvoiceNumber = String(rawInvoiceNumber)
          .replace(/[^a-zA-Z0-9_-]/g, '_')
          .substring(0, 50);
        const filename = `#${i + 1}_${safeInvoiceNumber}.${extension}`;
        link.download = filename;


        // Trigger download
        // Security Note: This is NOT a DOM XSS vulnerability (Snyk false positive).
        // - link.href is a blob URL from binary PDF data (not HTML content)
        // - link.download uses sanitized safeInvoiceNumber (alphanumeric only)
        // - No user content is injected as HTML - this is file download pattern
        // snyk:ignore javascript/DOMXSS - Blob URL from binary PDF data with sanitized filename; not exploitable as XSS
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Update progress
        setDownloadProgress(((i + 1) / itemsWithPdfs.length) * 100);

        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));

      } catch (error) {
        console.error(`Failed to download receipt for item ${i + 1}:`, error);
        alert(`Failed to download receipt #${i + 1}. Continuing with remaining downloads.`);
      }
    }


    setDownloadingPdfs(false);
    setDownloadProgress(100);

    // Auto-close modal after successful completion
    setTimeout(() => {
      onClose();
      setDownloadProgress(0);
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-0">
      {/* Capped to the viewport with the list scrolling between a fixed
          header and footer - a claim with many receipts ran off the screen. */}
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full sm:mx-4 flex flex-col max-h-[calc(100dvh-1.5rem)]">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-200 shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">Download Individual Receipts</h3>
          <button onClick={onClose} aria-label="Close" className="min-w-[44px] min-h-[44px] -m-2 sm:min-w-0 sm:min-h-0 sm:m-0 p-2 text-gray-400 hover:text-gray-600 transition-colors">&#10005;</button>
        </div>

        <div className="p-4 overflow-y-auto min-h-0">
          <p className="text-gray-600 mb-4">Download all individual receipts for this claim.</p>
          <p className="text-sm text-gray-500 mb-4">Each file will be named <code className="bg-gray-100 px-1 rounded whitespace-nowrap">#[number]_[invoiceNumber]</code> and keeps its own type (.pdf, .jpg or .png).</p>

          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <strong className="text-sm text-gray-700">Available receipts:</strong>
            <ul className="mt-2 space-y-1 text-sm text-gray-600">
              {expenseItems
                .filter(item => item.invoicePdfGoogleCloudUrl || item.pdfUrl)
                .map((item, index) => {
                  const invoiceNumber = item.invoiceId || item.receiptNumber || `item-${item.id}`;

                  if (isInfected(item)) {
                    return (
                      <li key={item.id} className="break-words">
                        #{index + 1}_{invoiceNumber} - <span className="font-medium text-red-700">{REVIEWER_INFECTED_MESSAGE}</span>
                      </li>
                    );
                  }
                  return (
                    <li key={item.id} className="break-words">
                      #{index + 1}_{invoiceNumber}.{documentExtension(item.pdfUrl || item.invoicePdfGoogleCloudUrl, item.contentType)} - {item.expenditureDescription || 'No description'}
                    </li>
                  );
                })
              }
            </ul>
          </div>

          {downloadingPdfs && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Downloading receipts... {Math.round(downloadProgress)}%</p>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-stanford-red transition-all duration-300"
                  style={{ width: `${downloadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 p-4 border-t border-gray-200 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={downloadAllPdfs}
            className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
            disabled={downloadingPdfs}
          >
            {downloadingPdfs ? 'Downloading...' : 'Download All Receipts'}
          </button>
        </div>
      </div>
    </div>
  );
};

// `questionsBanner`: shown above the status banners whatever the claim's
// status - the admin route passes the claim's open clarification questions.
const ExpenseForm = ({ adminMode = false, questionsBanner = null }) => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { confirm, alert } = useDialog();

  const {
    isAuditor: _isAuditorRole, isRegionalDirector, isGlobalDirector, isFinanceDirector,
  } = useContext(AuthContext);

  // Auditors open claims through the same /admin/ routes, so admin mode must be
  // role-aware - otherwise they'd get the admin editing/allocation controls.
  const [isAdminMode, setIsAdminMode] = useState(() => {
    return adminMode || window.location.pathname.includes('/admin/');
  });

  // Update admin mode when URL, prop, or role changes
  useEffect(() => {
    const newAdminMode = !_isAuditorRole &&
      (adminMode || window.location.pathname.includes('/admin/'));
    setIsAdminMode(newAdminMode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode, _isAuditorRole]); // window.location.pathname intentionally omitted (not a valid React dep)


  const invoiceService = new InvoiceService();
  const [cityInputs, setCityInputs] = useState({});
  const inputRefs = useRef({});
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [fileUploads, setFileUploads] = useState({});
  const { claimId } = useParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { user } = useContext(AuthContext);
   const [currentClaimId, setCurrentClaimId] = useState(claimId);
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [showInvoiceUploader, setShowInvoiceUploader] = useState(false);



  // API URL configuration
  const API_URL = getApiBaseUrl();

  // Create state for the dropdown portal element
  const [portalElement, _setPortalElement] = useState(null);
  const [_openComments, setOpenComments] = useState({});
  const [refreshingUrls, setRefreshingUrls] = useState(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  // The line whose description is being typed in, for the keep-the-labels hint.
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [showAdminPdfModal, setShowAdminPdfModal] = useState(false);
  // Reported by ClaimPtaeAllocation. The claim's Save is blocked while it is
  // true: adjusting a split and then saving the claim without saving the split
  // silently discarded the adjustment.
  const [ptaeAllocationDirty, setPtaeAllocationDirty] = useState(false);
  // Published by ClaimPtaeAllocation so the Excel export can carry each line's
  // PTAE. useCallback because it is a dependency of that component's loader.
  const [ptaeAllocation, setPtaeAllocation] = useState(null);
  const handleAllocationLoaded = useCallback((a) => setPtaeAllocation(a), []);

  // Downloading is a READ, so it is not tied to isAdminMode (which governs
  // editing and is forced false for auditors). Same for seeing the allocation:
  // gated on isAdminMode alone it never mounted for a reviewer, so their export
  // would have carried an empty PTAE column.
  const isClaimReviewer =
    _isAuditorRole || isRegionalDirector || isGlobalDirector || isFinanceDirector;
  const [pdfRenderKey, setPdfRenderKey] = useState(0);

  const _toggleCommentSection = (itemId) => {
    setOpenComments(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const toggleDescriptionExpansion = (itemId) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const {
    expenseItems,
  citySearch: _citySearch,
  setCitySearch,
  cityResults,
  activeSearchId,
  setActiveSearchId,
  setCityResults,
  isSearching: _isSearching,
  isLoading,
  setIsLoading,
  error,
  isSaving,
  saveSuccess,
  purposeAlertIds,
  addExpenseItem,
  deleteExpenseItem,
  deleteClaim,
  updateExpenseItem,
  searchCities,
  handleCitySelect,
  getExchangeRate,
  saveExpenses,
  debouncedSearch: _debouncedSearch,
  setDebouncedSearch,
  isViewMode: _isViewMode,
  isReadOnly,
  claimStatus,
  setClaimStatus,
  claimData,
  onBehalfName,
  setClaimData,
  adminComments,
  processedBy,
  processedAt,
  loadClaimData,
  isCreateRoute,
  showActionModal,
  setShowActionModal,
  selectedAction,
  setSelectedAction,
  adminDecisionComment,
  setAdminDecisionComment,
  handleAdminAction,
  withdrawApproval,
  handleModalSubmit,
  saveAdminChanges,
  lastName,
  setLastName,
  firstName,
  setFirstName,
  position,
  setPosition,
  navigate, setExpenseItems,
  } = useExpenseForm(adminMode);

  // -- The what/why slot -----------------------------------------------------
  // A description filled from an invoice is "who: X, what/why: , when: D", and
  // only the claimant can say what was bought and why. Each line's description
  // box is kept here so the form can take them straight to the slot.
  const descriptionRefs = useRef({});

  /** Caret just after "what/why: ", while the slot is still empty. */
  const placePurposeCaret = useCallback((el) => {
    if (!el || !needsPurpose(el.value)) return;
    const at = purposeCaretIndex(el.value);
    if (at >= 0) el.setSelectionRange(at, at);
  }, []);

  /** Scroll to a line's description and put the caret in its what/why slot. */
  const focusPurpose = useCallback((itemId) => {
    const el = descriptionRefs.current[itemId];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
    placePurposeCaret(el);
  }, [placePurposeCaret]);

  // A refused submit names the lines; go to the first of them.
  useEffect(() => {
    if (purposeAlertIds?.length) focusPurpose(purposeAlertIds[0]);
  }, [purposeAlertIds, focusPurpose]);

  // Lines still missing their purpose, for the count above the table.
  const purposeGaps = useMemo(
    () => (isReadOnly ? [] : linesNeedingPurpose(expenseItems)),
    [expenseItems, isReadOnly]
  );

  /**
   * Live USD total of the line items as currently edited.
   *
   * The PTAE allocation panel needs this rather than claimData.totalUsdAmount:
   * that is the last SAVED total, so adding or deleting a line item left the
   * panel measuring the split against a stale figure and an unbalanced split
   * looked balanced until approval refused it. Also feeds the total display
   * below, which previously recomputed the same sum inline.
   */
  const liveTotalUsd = useMemo(
    () => (expenseItems || []).reduce((sum, item) => {
      const amount = (item.usdEquivalent != null && !isNaN(item.usdEquivalent))
        ? Number(item.usdEquivalent)
        : 0;
      return sum + amount;
    }, 0),
    [expenseItems]
  );

  // Each receipt's USD value as the form has it now, so the allocation panel
  // follows an edit before it is saved rather than showing the stored amount.
  const liveLineValues = useMemo(() => Object.fromEntries(
    (expenseItems || []).map(item => [
      String(item.id),
      Math.round((Number(item.usdEquivalent) || 0) * 100) / 100,
    ])
  ), [expenseItems]);

  const timeoutRef = useRef(null);

  // -- Invoice reading -------------------------------------------------------
  // Each row is read on its own request after the upload, so rows appear at
  // once and fill in as their results arrive. Results are matched to rows by
  // invoiceId, never by position or row id: row ids change when a single
  // attach links a server row, and a replaced document gets a new invoiceId,
  // which is what lets a late result for the old one be dropped.
  const extractionQueueRef = useRef({ active: 0, waiting: [] });
  // invoiceIds whose row was deleted or whose document was replaced. A read
  // still waiting in the queue for one of these is skipped.
  const cancelledExtractionsRef = useRef(new Set());

  useEffect(() => {
    const queue = extractionQueueRef.current;
    // Leaving the form drops reads that have not started yet.
    return () => { queue.waiting.length = 0; };
  }, []);

  const enqueueExtraction = useCallback((job) => {
    const queue = extractionQueueRef.current;
    queue.waiting.push(job);
    const pump = () => {
      while (queue.active < EXTRACTION_CONCURRENCY && queue.waiting.length > 0) {
        const next = queue.waiting.shift();
        queue.active += 1;
        Promise.resolve()
          .then(next)
          .catch(() => { /* a failed read is reported on its row */ })
          .finally(() => {
            queue.active -= 1;
            pump();
          });
      }
    };
    pump();
  }, []);

  const applyExtraction = useCallback((invoiceId, result) => {
    // A row that was deleted, or whose document was replaced, no longer has
    // this invoiceId, so its late result matches nothing and is dropped.
    setExpenseItems(prev => prev.map(row => (
      row.invoiceId === invoiceId ? mergeExtractionIntoRow(row, result) : row
    )));
  }, [setExpenseItems]);

  const readInvoice = useCallback((invoiceId) => {
    if (!invoiceId) return;
    enqueueExtraction(async () => {
      if (cancelledExtractionsRef.current.has(invoiceId)) return;
      let result;
      try {
        result = await extractionService.extractInvoice(invoiceId);
      } catch (_err) {
        // Timeout, network or access error: the row says it could not be read.
        result = { status: 'failed' };
      }
      applyExtraction(invoiceId, result);
    });
  }, [enqueueExtraction, applyExtraction]);

  // Rows still being read, for the uploader's "Reading invoices…" line.
  const readingCount = useMemo(
    () => (expenseItems || []).filter(row => row.extractionStatus === 'pending').length,
    [expenseItems]
  );

  // Saving or submitting leaves the form, and nothing reads a stored invoice
  // again afterwards, so rows still being read would stay blank for good.
  // Ask first rather than lose those reads without a word.
  const confirmIfStillReading = async (action) => {
    if (readingCount === 0) return true;
    const what = readingCount === 1 ? '1 invoice is' : `${readingCount} invoices are`;
    return confirm(`${what} still being read. ${action} anyway? Anything not read yet will be left for you to fill in.`);
  };

  const {
    selectedInvoiceId: _selectedInvoiceId,
    setSelectedInvoiceId,
    pdfUrl,
    isExpanded: _isExpanded,
    zoomLevel: _zoomLevel,
    isLoading: _isPdfLoading,
    error: pdfError,
    zoomIn: _zoomIn,
    zoomOut: _zoomOut,
    resetZoom: _resetZoom,
    toggleExpanded: _toggleExpanded,
    loadPdfForInvoice: _loadPdfForInvoice,
    resetZoomOnNewPdf: _resetZoomOnNewPdf,
    setPdfUrl,
    setError
  } = usePdfViewer();

  // Create and manage portal element

    useEffect(() => {
      const processLocationBasedCurrency = () => {
        let hasUpdates = false;

        const updates = expenseItems.map(item => {
          if (item.location && !item.expenditureCurrency) {
            // Extract country from location
            const locationParts = item.location.split(',');
            const country = locationParts[locationParts.length - 1]?.trim();

            if (country) {
              const currencyCode = getCurrencyCodeForCountry(country);
              if (currencyCode && isValidCurrency(currencyCode)) {
                hasUpdates = true;
                return { ...item, expenditureCurrency: currencyCode };
              }
            }
          }
          return item;
        });

        // Only update if there are actual changes
        if (hasUpdates) {
          updates.forEach((item, index) => {
            if (item.expenditureCurrency !== expenseItems[index].expenditureCurrency) {
              updateExpenseItem(item.id, 'expenditureCurrency', item.expenditureCurrency);

              // Auto-fetch exchange rate if date is available and currency is not USD
              if (item.expenditureDate && item.expenditureCurrency !== 'USD') {
                setTimeout(() => {
                  getExchangeRate(item.id, item);
                }, 100); // Small delay to ensure state is updated
              }
            }
          });
        }
      };

      // Debounce the location processing to avoid too frequent updates
      const timeoutId = setTimeout(processLocationBasedCurrency, 300);

      return () => clearTimeout(timeoutId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expenseItems.length]); // Only depend on the length, not the entire array

    // Alternative: Create a separate effect that only runs when specific locations change
    const locationString = useMemo(() =>
      expenseItems.map(item => `${item.id}:${item.location || ''}`).join('|'),
      [expenseItems]
    );

    useEffect(() => {
      expenseItems.forEach(item => {
        if (item.location && !item.expenditureCurrency) {
          const locationParts = item.location.split(',');
          const country = locationParts[locationParts.length - 1]?.trim();

          if (country) {
            const currencyCode = getCurrencyCodeForCountry(country);
            if (currencyCode && isValidCurrency(currencyCode)) {
              updateExpenseItem(item.id, 'expenditureCurrency', currencyCode);

              if (item.expenditureDate && currencyCode !== 'USD') {
                setTimeout(() => {
                  getExchangeRate(item.id, { ...item, expenditureCurrency: currencyCode });
                }, 100);
              }
            }
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationString]); // This will only change when locations actually change

  const handleCurrencySelect = (itemId, currencyCode) => {
    updateExpenseItem(itemId, 'expenditureCurrency', currencyCode);

    // Auto-fetch exchange rate if we have all required data
    const item = expenseItems.find(exp => exp.id === itemId);
    if (item && item.expenditureDate && currencyCode !== 'USD') {
      getExchangeRate(itemId, { ...item, expenditureCurrency: currencyCode });
    }
  };


  // Add effect to auto-fill currency when location is selected
  useEffect(() => {
    expenseItems.forEach(item => {
      if (item.location && !item.expenditureCurrency) {
        // Extract country from location
        const locationParts = item.location.split(',');
        const country = locationParts[locationParts.length - 1]?.trim();

        if (country) {
          const currencyCode = getCurrencyCodeForCountry(country);
          if (currencyCode && isValidCurrency(currencyCode)) {
            updateExpenseItem(item.id, 'expenditureCurrency', currencyCode);

            // Auto-fetch exchange rate if date is available and currency is not USD
            if (item.expenditureDate && currencyCode !== 'USD') {
              getExchangeRate(item.id, { ...item, expenditureCurrency: currencyCode });
            }
          }
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseItems.map(item => item.location).join(',')]); // Re-run when locations change

  // the manual claim creation useEffect
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const mode = searchParams.get('mode');
    const claimIdFromParams = searchParams.get('claimId');

    // If we already have a claimId in the URL, don't create a new one
    if (claimIdFromParams || claimId || isCreateRoute) {
      return;
    }

    if (mode === 'manual') {
      const createClaimForManualEntry = async () => {
        try {
          setIsLoading(true);
          // Use axios to create the claim
          const response = await axios.post('/api/expenditure-claims', {
            status: 'draft'
          }, {
            withCredentials: true
          });

          if (response.data && response.data.status === 'success') {
            const newClaimId = response.data.id || response.data.data?.claimId;
            // CRITICAL: Navigate to the new URL with claimId
            navigate(`/claim/expense/${newClaimId}?mode=manual`, { replace: true });

          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error) {
          console.error('Error creating manual claim:', error);

          if (error.response?.status === 401) {
            setError('Authentication required. Please log in and try again.');
          } else {
            const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
            setError(`Failed to create claim: ${errorMessage}`);
          }
        } finally {
          setIsLoading(false);
        }
      };

      createClaimForManualEntry();
    }
  }, [claimId, isCreateRoute, navigate, setIsLoading, setError]); // Removed mode from dependencies

  // new useEffect to handle URL-based claimId from manual creation
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const mode = searchParams.get('mode');
    const urlClaimId = searchParams.get('claimId');

    // If we have a claimId in query params (from manual creation), use it
    if (mode === 'manual' && urlClaimId && !currentClaimId && !itemsLoaded) {
      setCurrentClaimId(urlClaimId);
      setClaimData({ claimId: urlClaimId });
      setClaimStatus('draft');
      setItemsLoaded(true); // Prevent other data loading

      // Add a default empty item for manual entry
      if (expenseItems.length === 0) {
        addExpenseItem();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClaimId, itemsLoaded, addExpenseItem, expenseItems.length]);


  // Function to get auth token from cookies
  const getAuthToken = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('authToken='))
      ?.split('=')[1];
    return token;
  };

  // Configure axios with token from cookies
  const getAxiosConfig = useCallback(() => {
    const token = getAuthToken();
    return {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      },
      withCredentials: true
    };
  }, []);

  // Function to refresh expired PDF URLs
  const _refreshPdfUrl = async (itemId) => {
    if (refreshingUrls.has(itemId)) return null;

    try {
      setRefreshingUrls(prev => new Set([...prev, itemId]));

      const response = await axios.get(`${API_URL}/api/refresh-pdf-url/${itemId}`, getAxiosConfig());

      if (response.data && response.data.pdfUrl) {
        updateExpenseItem(itemId, 'invoicePdfGoogleCloudUrl', response.data.pdfUrl);
        return response.data.pdfUrl;
      }
      throw new Error('No PDF URL in response');
    } catch (error) {
      console.error('Error refreshing PDF URL:', error);
      return null;
    } finally {
      setRefreshingUrls(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  // Generate a clean, properly formatted PDF using jsPDF
  const downloadFormOnly = async () => {
    const { jsPDF }             = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const {
      loadLogo, drawHeader, drawPageHeader, drawFooter, drawInfoBlock, drawApprovalChain,
      STANFORD_RED, LIGHT_GRAY, MID_GRAY,
    } = await import('../utils/claimPdfUtils.mjs');

    const doc    = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW  = doc.internal.pageSize.getWidth();
    const pageH  = doc.internal.pageSize.getHeight();
    const margin = 12;
    const TITLE  = 'EXPENSE REIMBURSEMENT CLAIM';

    const logoDataUrl = await loadLogo(seedlogo);
    let y = drawHeader(doc, logoDataUrl, TITLE, pageW, margin);

    // -- Claim metadata ------------------------------------------------------
    const ownerName = [
      claimData?.user?.firstName || claimData?.firstName || user?.firstName || firstName || '',
      claimData?.user?.lastName  || claimData?.lastName  || user?.lastName  || lastName  || '',
    ].filter(Boolean).join(' ');
    const ownerPosition = claimData?.user?.position || claimData?.position || user?.position || position || '';
    const claimIdDisplay = claimId || claimData?.claimId || '-';
    const statusDisplay  = (claimStatus || 'draft').toUpperCase();
    const totalUSD       = expenseItems.reduce((s, i) => s + (parseFloat(i.usdEquivalent) || 0), 0);

    const approvedByName = processedBy
      ? String(processedBy)
      : (claimStatus === 'approved' ? 'On file' : '-');
    const _approvedDate = processedAt
      ? new Date(processedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
      : '-';

    y = drawInfoBlock(doc, [
      ['Claim Owner:',      ownerName,      'Claim ID:',    claimIdDisplay],
      ['Position / Title:', ownerPosition,  'Status:',      statusDisplay],
      ['Total USD Amount:', `$${totalUSD.toFixed(2)}`, 'No. of Items:', String(expenseItems.length)],
      ['Approved By:',      approvedByName, null,           null],
    ], y, pageW, margin);

    // -- Expense items table -------------------------------------------------
    const columns = [
      { header: '#',           dataKey: 'num' },
      { header: 'Date',        dataKey: 'date' },
      { header: 'Receipt #',   dataKey: 'receipt' },
      { header: 'Location',    dataKey: 'location' },
      { header: 'Description', dataKey: 'description' },
      { header: 'Currency',    dataKey: 'currency' },
      { header: 'Amount',      dataKey: 'amount' },
      { header: 'Ex. Rate',    dataKey: 'rate' },
      { header: 'USD Equiv.',  dataKey: 'usd' },
      { header: 'Comments',    dataKey: 'comments' },
    ];

    const rows = expenseItems.map((item, idx) => {
      const raw = item.expenditureDate;
      const dateVal = raw
        ? new Date(raw).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
        : '-';
      return {
        num:         idx + 1,
        date:        dateVal,
        receipt:     item.receiptNumber || '-',
        location:    item.expenditureLocation || '-',
        description: item.expenditureDescription || '-',
        currency:    item.expenditureCurrency || '-',
        // A blank amount ('') is not a number: show '-' rather than "NaN".
        amount:      Number.isFinite(parseFloat(item.receiptAmount)) ? parseFloat(item.receiptAmount).toFixed(2) : '-',
        rate:        item.exchangeRate   != null ? parseFloat(item.exchangeRate).toFixed(2)   : '-',
        usd:         item.usdEquivalent  != null ? `$${parseFloat(item.usdEquivalent).toFixed(2)}` : '-',
        comments:    item.adminComments || item.comments || '',
      };
    });

    autoTable(doc, {
      startY: y,
      head: [columns.map(c => c.header)],
      body: rows.map(r => columns.map(c => r[c.dataKey])),
      styles: { fontSize: 7.5, cellPadding: 2, overflow: 'linebreak', lineColor: MID_GRAY, lineWidth: 0.2 },
      headStyles: { fillColor: STANFORD_RED, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8, halign: 'center' },
      alternateRowStyles: { fillColor: LIGHT_GRAY },
      columnStyles: {
        0: { halign: 'center', cellWidth: 7 },
        1: { cellWidth: 22 },
        2: { cellWidth: 18 },
        3: { cellWidth: 28 },
        4: { cellWidth: 'auto' },
        5: { halign: 'center', cellWidth: 16 },
        6: { halign: 'right',  cellWidth: 18 },
        7: { halign: 'right',  cellWidth: 18 },
        8: { halign: 'right',  cellWidth: 20, fontStyle: 'bold' },
        9: { cellWidth: 30 },
      },
      margin: { left: margin, right: margin },
      didDrawPage: (data) => { if (data.pageNumber > 1) drawPageHeader(doc, TITLE, pageW, margin, data.pageNumber); },
    });

    // -- Total ---------------------------------------------------------------
    const finalY = doc.lastAutoTable.finalY + 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...STANFORD_RED);
    doc.text(`TOTAL REIMBURSEMENT: $${totalUSD.toFixed(2)} USD`, pageW - margin, finalY, { align: 'right' });

    // -- Approval history ----------------------------------------------------
    // If not enough vertical room, push onto a new page
    let chainStartY = finalY + 10;
    if (chainStartY > pageH - 50) {
      doc.addPage();
      drawPageHeader(doc, TITLE, pageW, margin, doc.internal.pages.length - 1);
      chainStartY = 16;
    }
    drawApprovalChain(doc, claimData?.approvalChain, chainStartY, pageW, margin, {
      name:   processedBy ? String(processedBy) : null,
      role:   'admin',
      date:   processedAt,
      status: claimStatus,
    });

    // -- Footer --------------------------------------------------------------
    drawFooter(doc, claimIdDisplay, pageH, pageW);

    doc.save(`Expense_Claim_${claimIdDisplay}_${new Date().toISOString().slice(0, 10)}.pdf`);

    if (isAdminMode) setTimeout(() => setShowAdminPdfModal(true), 300);
  };

  // Determine if actions should be hidden (approved claims)
  const shouldHideActions = claimStatus === 'approved';

  // An approved claim is final and must be locked for everyone, admins
  // included. `isReadOnly` alone is false in admin mode, so every line-item
  // and traveller field stayed typable on an approved claim - edits that
  // went nowhere but looked to the admin as though they had taken.
  const effectiveIsReadOnly = isReadOnly || shouldHideActions;

  // Open PDF in a modal/popup using backend URL
  const openPdfDetails = async (itemId) => {
    const item = expenseItems.find(item => item.id === itemId);
    if (!item) {
      console.error("Item not found:", itemId);
      return;
    }

    // Removed by the virus scan. The View buttons are already disabled, but
    // never hand a stale stored path to the preview frame.
    if (isInfected(item)) return;

    if (!item.invoicePdfGoogleCloudUrl && !item.pdfUrl) {
      console.error("No PDF URL available for this item");
      setSelectedInvoiceId(item.id);
      setPdfUrl(null);
      setError("No PDF document is attached to this expense");
      setShowPdfModal(true);
      return;
    }

    // Use the signed URL from the item directly
    const signedPdfUrl = item.pdfUrl || item.invoicePdfGoogleCloudUrl || null;

    setSelectedInvoiceId(item.id);
    setPdfUrl(signedPdfUrl);
    setError(null);
    setShowPdfModal(true);
};

  // Close the PDF modal
  const closePdfModal = () => {
    setShowPdfModal(false);
  };

  // Date fields use <DateInput>, which owns timezone-safe parsing/formatting.

  // Handle city input change
  const handleCityInputChange = (itemId, value) => {
    if (isReadOnly) return;

    setCityInputs(prev => ({
      ...prev,
      [itemId]: value
    }));

    // Persist what was typed onto the item too. Previously the location only
    // reached the claim when a city was picked from the dropdown, so a
    // free-typed location was shown in the box but silently never saved.
    updateExpenseItem(itemId, 'expenditureLocation', value);

    setCitySearch(value);
    setDebouncedSearch(value);
    setActiveSearchId(itemId);

    setTimeout(() => positionDropdown(itemId), 0);
  };

  // Handle city focus
  const handleCityInputFocus = (itemId, currentLocation) => {
    if (isReadOnly) return;

    const inputValue = cityInputs[itemId] ?? currentLocation ?? '';

    setCityInputs(prev => ({
      ...prev,
      [itemId]: inputValue
    }));

    setCitySearch(inputValue);
    setDebouncedSearch(inputValue);
    setActiveSearchId(itemId);

    setTimeout(() => {
      positionDropdown(itemId);
      searchCities(inputValue, itemId);
    }, 0);
  };

  // Handle city selection
  const handleCitySelection = (itemId, cityName, country) => {
    if (isReadOnly) return;

    handleCitySelect(itemId, cityName, country); // This calls the hook's function

    setCityInputs(prev => ({
      ...prev,
      [itemId]: cityName + (country ? `, ${country}` : '')
    }));

    setActiveSearchId(null);
    setCityResults([]);
  };

  // Function to position the dropdown correctly
  const positionDropdown = useCallback((itemId) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const inputElement = itemId === 'quickFill'
        ? document.getElementById('quickFillCityInput')
        : inputRefs.current[itemId];

      if (!inputElement) {
        return;
      }

      const rect = inputElement.getBoundingClientRect();

      // At least 300px wide, but never past the screen edge: on a phone the
      // location field is ~150px and anchoring at its left ran the list off
      // the right side.
      const vw = document.documentElement.clientWidth || window.innerWidth;
      const width = Math.min(Math.max(rect.width, 300), vw - 16);
      const position = {
        top: rect.bottom + 5,
        left: Math.min(Math.max(rect.left, 8), vw - 8 - width),
        width
      };

      setDropdownPosition(position);
    }, 0);
  }, []);

  // Handle window resize and scroll
  useEffect(() => {
    const handleResize = () => {
      if (activeSearchId) {
        positionDropdown(activeSearchId);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [activeSearchId, positionDropdown]);


  // Updated the handleFileAttachment function to use the current claimId
  const handleFileAttachment = async (itemId, event) => {
  if (isReadOnly) return;

  const file = event.target.files[0];
  if (!file) return;

  // PDF, JPEG or PNG, by MIME type OR extension: some sources hand over an
  // empty or generic type. The server checks the file's real bytes either way.
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  const isAllowed = allowedTypes.includes(String(file.type || '').toLowerCase())
    || /\.(pdf|jpe?g|png)$/i.test(String(file.name || ''));
  if (!isAllowed) {
    event.target.value = '';
    alert('Please upload a PDF, JPG or PNG file.');
    return;
  }

  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    event.target.value = '';
    alert('File size must be less than 10MB.');
    return;
  }

  try {
    setFileUploads(prev => ({
      ...prev,
      [itemId]: { uploading: true, phase: 'uploading', name: file.name }
    }));

    const getCurrentClaimId = () => {
      const urlPath = window.location.pathname;
      const urlClaimIdMatch = urlPath.match(/\/claim\/expense\/([a-fA-F0-9]{24})/);
      if (urlClaimIdMatch) return urlClaimIdMatch[1];

      const searchParams = new URLSearchParams(window.location.search);
      const urlClaimId = searchParams.get('claimId');
      if (urlClaimId) return urlClaimId;

      return claimId || claimData?.claimId;
    };

    const currentWorkingClaimId = getCurrentClaimId();

    // claimId is optional now - on a fresh form the backend lazily creates a
    // draft claim and returns its id. This request only stores the file; where
    // invoices are read for the claim's owner, reading is a separate request
    // made after it returns.
    const result = await invoiceService.uploadManualInvoiceWithClaimId(file, itemId, currentWorkingClaimId);

    if (result && result.success && result.invoiceId && result.pdfUrl) {
      const backendItemId = result.itemId || itemId;

      // The server says whether this claim's owner gets invoices read.
      const readIt = result.extractionEnabled === true;

      // The document is linked to the row. Nothing the user typed is
      // overwritten: a read (where enabled) fills only the fields still empty.
      setExpenseItems(prevItems =>
        prevItems.map(item => {
          if (item.id !== itemId) return item;
          // A replaced document's read, queued or running, is for a file no
          // longer on this row: skip it, and its late result matches nothing
          // once the row carries the new invoiceId.
          if (item.invoiceId && item.invoiceId !== result.invoiceId) {
            cancelledExtractionsRef.current.add(item.invoiceId);
          }
          const { extractionStatus: _previousStatus, ...rest } = item;
          return {
            ...rest,
            id: backendItemId,
            invoiceId: result.invoiceId,
            invoicePdfGoogleCloudUrl: result.pdfUrl,
            pdfUrl: result.pdfUrl,
            // A new file starts a new scan. Without this a row replacing an
            // infected file would keep the old 'infected' flag and hide the
            // fresh upload behind the removed-file notice.
            scanStatus: result.scanStatus || 'pending',
            ...(readIt ? { extractionStatus: 'pending' } : {}),
          };
        })
      );
      if (readIt) readInvoice(result.invoiceId);

      // Persist the (possibly newly-created) claim id so Submit updates it.
      if (result.claimId) {
        setClaimData(prev => ({ ...(prev || {}), claimId: result.claimId }));
      }

      // Update file upload state
      setFileUploads(prev => ({
        ...prev,
        [itemId]: {
          uploaded: true,
          name: file.name,
          url: result.pdfUrl,
          invoiceId: result.invoiceId,
          backendItemId: backendItemId
        }
      }));

      // Force re-render of PDF sections
      setPdfRenderKey(prev => prev + 1);

      // Additional force update after a short delay
      setTimeout(() => {
        setPdfRenderKey(prev => prev + 1);
      }, 500);

    } else {
      throw new Error('Invalid response from upload service');
    }

  } catch (error) {
    console.error('File upload failed:', error);

    setFileUploads(prev => {
      const newState = { ...prev };
      delete newState[itemId];
      return newState;
    });

    alert(`Failed to upload document: ${error.message}`);
  }

  event.target.value = '';
};


  // Function to trigger file input click
  const triggerFileInput = (itemId) => {
    if (isReadOnly) return;

    const fileInput = document.getElementById(`file-input-${itemId}`);
    if (fileInput) {
      fileInput.click();
    }
  };

  // Enhanced helper function to show full description with better formatting
  const renderDescription = (item) => {
    const description = item.expenditureDescription || '';
    const isExpanded = expandedDescriptions.has(item.id);
    const shouldTruncate = description.length > 100;

    if (isReadOnly) {
      return (
        <div>
          <div className={isExpanded ? '' : 'line-clamp-3'}>
            {isExpanded || !shouldTruncate
              ? description
              : `${description.substring(0, 100)}...`
            }
          </div>
          {shouldTruncate && (
            <button
              className="min-h-[44px] lg:min-h-0 text-sm text-stanford-red hover:text-stanford-light-red mt-1"
              onClick={() => toggleDescriptionExpansion(item.id)}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      );
    } else {
      // Amber while the line does not yet say what was bought and why; red
      // once a submit has been refused for it. Either way the note says
      // exactly what is missing, and it goes as soon as the what/why slot
      // says something.
      const missingPurpose = needsPurpose(description);
      const refused = missingPurpose && (purposeAlertIds || []).includes(item.id);
      const noteId = `purpose-note-${item.id}`;
      // The claimant adds to the description's structure or edits after each
      // label - never deletes "what/why:" or "when:" (the user, 2026-10-21).
      // A deleted label is said at once, with a way to put it back, rather
      // than leaving the claimant to guess the format the submit check wants.
      const lostLabels = missingStructureLabels(description);
      const restoreStructure = () => {
        updateExpenseItem(item.id, 'expenditureDescription', withStructure(description));
        // After the new value has rendered.
        setTimeout(() => focusPurpose(item.id), 0);
      };
      const editing = editingDescriptionId === item.id;
      const showNote = lostLabels.length > 0 || missingPurpose || editing;
      const edge = refused
        ? 'border-red-500 ring-1 ring-red-500'
        : (missingPurpose || lostLabels.length > 0) ? 'border-amber-400 ring-1 ring-amber-300' : 'border-gray-300';
      return (
        <div>
          <textarea
            ref={(el) => { descriptionRefs.current[item.id] = el; }}
            value={description}
            onChange={(e) => updateExpenseItem(item.id, 'expenditureDescription', e.target.value)}
            // Clicking into a stub lands the caret in the empty slot, not at
            // the end after "when:". Deferred: the browser places the caret
            // from the click after focus fires.
            onFocus={(e) => {
              const el = e.target;
              setEditingDescriptionId(item.id);
              if (needsPurpose(el.value)) setTimeout(() => placePurposeCaret(el), 0);
            }}
            onBlur={() => setEditingDescriptionId(cur => (cur === item.id ? null : cur))}
            aria-invalid={missingPurpose || lostLabels.length > 0 || undefined}
            aria-describedby={showNote ? noteId : undefined}
            placeholder="Describe the expense: Who was involved, what was purchased, why was it necessary..."
            className={`w-full px-3 lg:px-2 py-2 lg:py-1 text-sm border ${edge} rounded-lg lg:rounded resize-none h-20 lg:h-16 overflow-y-auto focus:ring-2 focus:ring-stanford-red focus:border-transparent`}
            rows="3"
          />
          {lostLabels.length > 0 ? (
            <p id={noteId} className={`mt-1 text-xs ${refused ? 'text-red-600 font-medium' : 'text-amber-700'}`}>
              Don&apos;t delete the {lostLabels.map(l => `"${l}"`).join(' and ')} label{lostLabels.length === 1 ? '' : 's'}.
              {' '}Add to the description or edit what follows each label.{' '}
              <button
                type="button"
                onClick={restoreStructure}
                className="underline font-medium hover:text-stanford-red focus:outline-none focus-visible:ring-2 focus-visible:ring-stanford-red rounded"
              >
                Put {lostLabels.length === 1 ? 'it' : 'them'} back
              </button>
            </p>
          ) : missingPurpose ? (
            <p id={noteId} className={`mt-1 text-xs ${refused ? 'text-red-600 font-medium' : 'text-amber-700'}`}>
              {refused ? 'Needed to submit: add' : 'Add'} what was bought and why after &quot;what/why:&quot;.
            </p>
          ) : editing ? (
            <p id={noteId} className="mt-1 text-xs text-gray-500">
              Keep the &quot;who:&quot;, &quot;what/why:&quot; and &quot;when:&quot; labels: add to them or edit what follows, but don&apos;t delete them.
            </p>
          ) : null}
        </div>
      );
    }
  };

  // Merge invoices extracted by the embedded uploader into the form in place
  // (instead of the standalone uploader navigating away to the form).
  // With `extractionEnabled` (the server's call, from the claim owner's region)
  // each new row is then read on its own request and fills in as it returns.
  const handleInvoicesExtracted = useCallback((items, newClaimId, { extractionEnabled = false } = {}) => {
    if (newClaimId) {
      setClaimData(prev => ({ ...(prev || {}), claimId: newClaimId }));
    }
    if (Array.isArray(items) && items.length > 0) {
      const mapped = items.map(it => ({
        // The DB row id, so attaching a document to this row later updates the
        // row instead of creating a second one. Older responses had no id and
        // fell back to invoiceId, which the attach path could not match.
        id: it.id || it.invoiceId || crypto.randomUUID(),
        invoiceId: it.invoiceId,
        contentType: it.contentType || null,
        // Leave blank rather than defaulting to today - these are placeholder
        // rows the user fills in against the attached invoice, and Date is a
        // required field, so a silent "today" would sail through validation
        // with the wrong value. Local midnight, like DateInput's own values:
        // new Date('YYYY-MM-DD') is UTC midnight, a day early west of UTC.
        expenditureDate: parseLocalDate(it.expenditureDate),
        receiptNumber: it.receiptNumber || '',
        expenditureLocation: it.expenditureLocation || '',
        expenditureCurrency: it.expenditureCurrency || '',
        // The template when the server sent no stub, so the what/why slot
        // the submit check asks for is there; a later read still fills it.
        expenditureDescription: it.expenditureDescription || DESCRIPTION_TEMPLATE,
        // Blank, not 0, when there is no amount yet: the box then shows empty
        // for the user to fill, and a blank amount is saved as null.
        receiptAmount: Number(it.receiptAmount) > 0 ? it.receiptAmount : '',
        exchangeRate: it.exchangeRate || 0,
        usdEquivalent: it.usdEquivalent || 0,
        comments: it.comments || '',
        invoicePdfGoogleCloudUrl: it.invoicePdfGoogleCloudUrl || it.pdfUrl || null,
        pdfUrl: it.pdfUrl || it.invoicePdfGoogleCloudUrl || null,
        scanStatus: it.scanStatus,
        status: it.status || 'pending',
        ...(extractionEnabled && it.invoiceId ? { extractionStatus: 'pending' } : {})
      }));
      setExpenseItems(prev => [...prev, ...mapped]);
      if (extractionEnabled) {
        mapped.forEach(row => { if (row.invoiceId) readInvoice(row.invoiceId); });
      }
    }
  }, [setClaimData, setExpenseItems, readInvoice]);

  const inputBaseClass = "w-full min-h-[44px] lg:min-h-0 px-3 lg:px-2 py-2 lg:py-1 text-sm border border-gray-300 rounded-lg lg:rounded focus:ring-2 focus:ring-stanford-red focus:border-transparent";
  const readOnlyClass = "bg-gray-100 cursor-not-allowed";

  // This form owns the page frame for every route that shows it (the claimant
  // routes via ExpenseFormPage and the admin route via AdminExpenseEdit, which
  // render no frame of their own), because the band title depends on who is
  // looking and whose claim it is - things only this component knows.
  const viewingOthersClaim = !!(claimData?.user && claimData.user.id !== user?.id);
  const pageTitle = isAdminMode && !_isAuditorRole
    ? 'Admin Edit: Expense Claim'
    : (isClaimReviewer && (adminMode || viewingOthersClaim))
      ? 'Expense Claim'
      : 'Expense Reimbursement';
  // The instructions that used to open the page in a white card - addressed to
  // the claimant ("your invoices"), so only the claimant's view shows them, and
  // as before not once the claim is approved, where they contradicted the
  // locked state ("add additional expenses manually" on a claim nobody can edit).
  const pageSubtitle = pageTitle === 'Expense Reimbursement' && claimStatus !== 'approved'
    ? 'Review the information extracted from your invoices and complete any missing details. You can add additional expenses manually if needed.'
    : undefined;

  return (
    <PageShell title={pageTitle} subtitle={pageSubtitle} width="7xl" photo={false}>
      {isLoading && (
        <div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin"></div>
        </div>
      )}

      {/* Filing for someone else. Said first, in their name, because the rest
          of the page is the ordinary claim form - which is the point, and also
          exactly why it needs saying. */}
      {onBehalfName && (
        <div className="bg-indigo-50 border border-indigo-200 text-indigo-900 dark:bg-indigo-950 dark:border-indigo-800 dark:text-indigo-100 rounded-xl shadow-lg px-4 py-3 text-sm mb-4 sm:mb-6">
          <p className="font-semibold">Filing an expense claim for {onBehalfName}</p>
          <p className="mt-0.5 text-indigo-800 dark:text-indigo-200">
            It is their claim. A saved draft stays hidden from them; submitting sends it
            for review and emails them to say you filed it.
          </p>
        </div>
      )}

      {/* The fill-in instructions now sit in the page band (pageSubtitle). */}

      {/* One banner for one state, matching the per-diem page. */}
      {claimStatus === 'approved' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold text-green-800 mb-2">Approved Claim</h3>
          {(isAdminMode || isClaimReviewer) && (
            <p className="text-green-700 mb-2">This claim is approved, so its details are locked.</p>
          )}
          <p className="text-green-700 mb-4">Download the expense claim form {isAdminMode ? '(individual receipts available after download)' : 'without embedded receipts for cleaner printing'}.</p>
          <button
            onClick={downloadFormOnly}
            className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            aria-label="Download expense claim form as PDF"
          >
            Download Claim Form
          </button>
        </div>
      )}

      {/* Traveler Information Section */}
      <div className="bg-white rounded-xl shadow-lg p-5 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Traveler Information</h2>

        <div className="flex flex-wrap items-start gap-4 mb-4">
          <div className="text-sm font-medium text-gray-700 w-full sm:w-auto sm:min-w-[140px] sm:pt-2">Traveler Name:</div>
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3 sm:gap-4 flex-1 w-full sm:w-auto">
            <div className="flex flex-col sm:min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1">Last/Surname</label>
              <input
                type="text"
                value={
                  claimData?.user?.lastName ||
                  claimData?.lastName ||
                  (isAdminMode && claimData ? '' : (user.lastName || lastName))
                }
                onChange={(e) => setLastName(e.target.value)}
                readOnly={
                  effectiveIsReadOnly ||
                  (isAdminMode && claimData?.user && claimData.user.id !== user.id) ||
                  (claimData?.user && claimData.user.id !== user.id)
                }
                className={`${inputBaseClass} ${
                  (isReadOnly ||
                  (isAdminMode && claimData?.user && claimData.user.id !== user.id) ||
                  (claimData?.user && claimData.user.id !== user.id)) ? readOnlyClass : ""
                }`}
                aria-label="Last name"
                placeholder={isAdminMode ? "Traveler's last name" : "Your last name"}
              />
            </div>
            <div className="flex flex-col sm:min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1">First/Given</label>
              <input
                type="text"
                value={
                  claimData?.user?.firstName ||
                  claimData?.firstName ||
                  (isAdminMode && claimData ? '' : (user.firstName || firstName))
                }
                onChange={(e) => setFirstName(e.target.value)}
                readOnly={
                  effectiveIsReadOnly ||
                  (isAdminMode && claimData?.user && claimData.user.id !== user.id) ||
                  (claimData?.user && claimData.user.id !== user.id)
                }
                className={`${inputBaseClass} ${
                  (isReadOnly ||
                  (isAdminMode && claimData?.user && claimData.user.id !== user.id) ||
                  (claimData?.user && claimData.user.id !== user.id)) ? readOnlyClass : ""
                }`}
                aria-label="First name"
                placeholder={isAdminMode ? "Traveler's first name" : "Your first name"}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-4">
          <div className="text-sm font-medium text-gray-700 sm:min-w-[140px]">Position:</div>
          <input
            type="text"
            className={`flex-1 ${inputBaseClass} ${
              (isReadOnly ||
              (isAdminMode && claimData?.user && claimData.user.id !== user.id) ||
              (claimData?.user && claimData.user.id !== user.id)) ? readOnlyClass : ""
            }`}
            value={
              claimData?.user?.position ||
              claimData?.position ||
              (isAdminMode && claimData ? '' : (user.position || position))
            }
            onChange={(e) => setPosition(e.target.value)}
            readOnly={
              effectiveIsReadOnly ||
              (isAdminMode && claimData?.user && claimData.user.id !== user.id) ||
              (claimData?.user && claimData.user.id !== user.id)
            }
            aria-label="Position"
            placeholder={isAdminMode ? "Traveler's position" : "Your position"}
          />
        </div>
      </div>

      {questionsBanner}

      {/* Status banners - only for an existing, non-draft claim opened by URL
          (never during creation, not while it's still a draft, and not once the
          claim is approved: an approved claim needs no admin feedback banner
          for any role). */}
      {!isCreateRoute && (claimId || currentClaimId) && claimStatus !== 'draft' && claimStatus !== 'approved' && (
        <>
          {!isAdminMode && (
            <ClaimStatusBanner claimStatus={claimStatus} />
          )}

          <AdminFeedbackSection
            claimStatus={claimStatus}
            adminComments={adminComments}
            processedAt={processedAt}
            processedBy={processedBy}
          />
        </>
      )}

      {/* Invoice upload - collapsible; adds a placeholder line per invoice.
          Admins get this on submitted/under-review claims too (the backend
          allows it), but not once the claim is approved. */}
      {!isReadOnly && !shouldHideActions && (
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Upload Invoices <span className="text-sm font-normal text-gray-500">(optional)</span></h3>
              <p className="text-sm text-gray-600">Upload invoices (PDF, JPG or PNG) to create one line item per invoice, then fill in the details below while viewing each document.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowInvoiceUploader(v => !v)}
              className="min-h-[44px] px-4 py-2 border border-stanford-red text-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors whitespace-nowrap"
            >
              {showInvoiceUploader ? 'Hide' : 'Upload Invoices'}
            </button>
          </div>
          {showInvoiceUploader && (
            <div className="mt-4">
              <InvoiceUploader onExtracted={handleInvoicesExtracted} claimId={claimData?.claimId || currentClaimId || claimId} readingCount={readingCount} />
            </div>
          )}
        </div>
      )}

      {/* Main form content */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-4 sm:mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Expense Items</h3>
          {!isReadOnly && !shouldHideActions && (
            <button
              onClick={addExpenseItem}
              className="min-h-[44px] px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors"
            >
              + Add Expense
            </button>
          )}
        </div>

        {/* How a description is written, before anyone starts editing one: the
            structure is added to or edited, never deleted (2026-10-21). */}
        {!isReadOnly && !shouldHideActions && (
          <p className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs text-gray-600">
            Each description reads <span className="font-mono">who: …, what/why: …, when: …</span>.
            {' '}Write after each label or edit what is there, but don&apos;t delete the
            {' '}<span className="font-mono">what/why:</span> or <span className="font-mono">when:</span> labels.
          </p>
        )}

        {/* Lines still missing what was bought and why - usually rows filled
            from an invoice, whose description is only "who: X, what/why: ,
            when: D". Each link goes to that line's what/why slot. */}
        {purposeGaps.length > 0 && (
          <div role="status" className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-800">
            <span>
              {purposeGaps.length === 1 ? '1 line still needs' : `${purposeGaps.length} lines still need`} what was bought and why:
            </span>
            {purposeGaps.map((gap, i) => (
              <React.Fragment key={gap.id}>
                {i > 0 && <span aria-hidden="true">·</span>}
                <button
                  type="button"
                  onClick={() => focusPurpose(gap.id)}
                  className="min-h-[44px] sm:min-h-0 font-medium underline underline-offset-2 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded"
                >
                  Line {gap.line}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Desktop: one wide row per item, scrolling sideways if it must.
            Below lg each row re-flows into a labelled card (see .line-item-card
            in index.css) - the 1100px row was unusable on a phone. */}
        <div className="lg:overflow-x-auto">
          <div className="lg:min-w-[1100px]">
            <div className={`hidden lg:grid ${shouldHideActions ? 'lg:grid-cols-[110px_120px_110px_85px_minmax(160px,1.4fr)_90px_90px_90px_minmax(120px,0.9fr)]' : 'lg:grid-cols-[110px_120px_110px_85px_minmax(160px,1.4fr)_90px_90px_90px_minmax(120px,0.9fr)_120px]'} gap-2 p-3 bg-stanford-red text-white text-sm font-semibold`} role="row">
              <div role="columnheader">Date</div>
              <div role="columnheader">Receipt # <span className="text-red-200" title="Required">*</span></div>
              <div role="columnheader">Location <span className="text-red-200" title="Required">*</span></div>
              <div role="columnheader" className="pl-2">Currency</div>
              <div role="columnheader" className="pl-2">
                Description<br/>
                <small className="text-gray-300 font-normal">Who, What, Why</small>
              </div>
              <div role="columnheader">Amount</div>
              <div role="columnheader">Ex. Rate</div>
              <div role="columnheader">USD</div>
              <div role="columnheader">Comments</div>
              {!shouldHideActions && <div role="columnheader">Actions</div>}
            </div>

            {expenseItems.map((item, _index) => (
              <div key={item.invoiceId || item.id || crypto.randomUUID()}>
                <div className={`line-item-card grid grid-cols-2 ${shouldHideActions ? 'lg:grid-cols-[110px_120px_110px_85px_minmax(160px,1.4fr)_90px_90px_90px_minmax(120px,0.9fr)]' : 'lg:grid-cols-[110px_120px_110px_85px_minmax(160px,1.4fr)_90px_90px_90px_minmax(120px,0.9fr)_120px]'} gap-3 lg:gap-2 p-4 lg:p-3 border-b border-gray-200 items-start text-sm lg:hover:bg-gray-50`} role="row">
                  <div data-label="Date">
                    <DateInput
                      value={item.expenditureDate}
                      onChange={(d) => updateExpenseItem(item.id, 'expenditureDate', d)}
                      className={`${inputBaseClass} ${isReadOnly ? readOnlyClass : ""}`}
                      readOnly={effectiveIsReadOnly}
                    />
                  </div>
                  <div className="col-span-1" data-label="Receipt #" data-required>
                    <input
                      type="text"
                      value={item.receiptNumber || ''}
                      onChange={(e) => updateExpenseItem(item.id, 'receiptNumber', e.target.value)}
                      className={`${inputBaseClass} ${isReadOnly ? readOnlyClass : ""}`}
                      readOnly={effectiveIsReadOnly}
                      placeholder="Receipt #"
                    />
                  </div>
                  <div className="col-span-1" data-label="Location" data-required>
                    <div className="relative">
                      <input
                        ref={el => inputRefs.current[item.id] = el}
                        type="text"
                        // An empty box falls through to the row's value: a city
                        // read from the invoice lands on the row after a focus
                        // may already have cached '' here, and the box must show
                        // what will be saved. Typing keeps both in step.
                        value={cityInputs[item.id] || item.expenditureLocation || ''}
                        onChange={(e) => handleCityInputChange(item.id, e.target.value)}
                        onFocus={() => handleCityInputFocus(item.id, item.expenditureLocation)}
                        placeholder="Search city"
                        className={`${inputBaseClass} ${isReadOnly ? readOnlyClass : ""}`}
                        readOnly={effectiveIsReadOnly}
                      />
                    </div>
                  </div>
                  <div className="col-span-1" data-label="Currency">
                    <CurrencyDropdown
                      value={item.expenditureCurrency || ''}
                      onChange={(value) => updateExpenseItem(item.id, 'expenditureCurrency', value)}
                      onCurrencySelect={(currencyCode) => handleCurrencySelect(item.id, currencyCode)}
                      itemId={item.id}
                      isReadOnly={isReadOnly}
                      onAutoFetchRate={(currencyCode) => {
                        if (item.expenditureDate && currencyCode !== 'USD') {
                          getExchangeRate(item.id, { ...item, expenditureCurrency: currencyCode });
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-2 lg:col-span-1" data-label="Description (who, what, why)">
                    {renderDescription(item)}
                  </div>
                  <div className="col-span-1" data-label="Amount">
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      value={item.receiptAmount || ''}
                      onChange={(e) => updateExpenseItem(item.id, 'receiptAmount', e.target.value)}
                      // line-item-amount: sized like the text boxes beside it
                      // (index.css) - a number input missed their rule.
                      className={`line-item-amount ${inputBaseClass} ${isReadOnly ? readOnlyClass : ""}`}
                      readOnly={effectiveIsReadOnly}
                      placeholder="0.00"
                    />
                  </div>
                   <div className="col-span-1" data-label="Ex. rate">
                    <div className="flex items-center gap-1">
                      {/* Auto-fetched - read-only for everyone, shown to 2dp. */}
                      <input
                        type="text"
                        value={(item.exchangeRate != null && item.exchangeRate !== '' && !isNaN(item.exchangeRate)) ? Number(item.exchangeRate).toFixed(2) : ''}
                        className={`${inputBaseClass} ${readOnlyClass}`}
                        readOnly
                        tabIndex={-1}
                        title="Exchange rate is fetched automatically for the expense date"
                        placeholder="-"
                      />
                    </div>
                  </div>

                  {/* On desktop the figure sits in a box sized like the inputs
                      either side (8px padding, a transparent 1px border, 14px
                      text), so it lines up with the amount and rate. */}
                  <div className="col-span-2 lg:col-span-1 font-medium text-gray-800 tabular-nums line-item-usd lg:px-2 lg:py-2 lg:border lg:border-transparent lg:text-sm" data-label="USD">
                    ${(item.usdEquivalent != null && !isNaN(item.usdEquivalent)) ? Number(item.usdEquivalent).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </div>
                  <div className="col-span-2 lg:col-span-1" data-label="Comments">
                    <textarea
                      value={item.comments || ''}
                      onChange={(e) => updateExpenseItem(item.id, 'comments', e.target.value)}
                      placeholder={(!(item.invoicePdfGoogleCloudUrl || item.pdfUrl || fileUploads[item.id]?.uploaded) && item.noReceipt) ? 'Required: explain the missing receipt…' : 'Notes...'}
                      className={`${inputBaseClass} resize-none h-16 ${isReadOnly ? readOnlyClass : ""}`}
                      readOnly={effectiveIsReadOnly}
                      rows="2"
                    />
                    {!isReadOnly && !(item.invoicePdfGoogleCloudUrl || item.pdfUrl || fileUploads[item.id]?.uploaded) && (
                      <label className="flex items-center lg:items-start gap-2 lg:gap-1 min-h-[44px] lg:min-h-0 mt-1 lg:mt-1 text-sm lg:text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          className="lg:mt-0.5 w-5 h-5 lg:w-auto lg:h-auto accent-stanford-red"
                          checked={!!item.noReceipt}
                          onChange={(e) => updateExpenseItem(item.id, 'noReceipt', e.target.checked)}
                        />
                        <span>No receipt available</span>
                      </label>
                    )}
                  </div>


                  {/* Actions column */}
                  {!shouldHideActions && (() => {
                    const hasFile = fileUploads[item.id]?.uploaded || item.invoicePdfGoogleCloudUrl || item.pdfUrl;
                    // Infected files still count as "has a file" so the attach
                    // button reads Replace, but there is nothing left to view.
                    const rowInfected = isInfected(item);
                    return (
                    <div className="col-span-2 lg:col-span-1 flex flex-col gap-2 lg:gap-1 pt-1 lg:pt-0">
                      <div className="grid grid-cols-2 gap-2 lg:gap-1">
                        <button
                          type="button"
                          aria-label="View document"
                          className={`w-full min-h-[44px] lg:min-h-0 p-2 text-sm rounded-lg lg:rounded border transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 ${hasFile ? 'text-amber-900 bg-amber-400 border-amber-500 hover:bg-amber-500' : 'text-gray-400'}`}
                          onClick={() => openPdfDetails(item.id)}
                          disabled={rowInfected || (!item.invoicePdfGoogleCloudUrl && !item.pdfUrl)}
                          title={hasFile ? 'View uploaded document' : 'View document'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          <span className="lg:hidden">View</span>
                        </button>
                        {!isReadOnly && (
                          <button
                            type="button"
                            aria-label={hasFile ? 'Replace attached document' : 'Attach document'}
                            className={`w-full min-h-[44px] lg:min-h-0 p-2 text-sm rounded-lg lg:rounded border transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 ${hasFile ? 'text-amber-900 bg-amber-400 border-amber-500 hover:bg-amber-500' : 'text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => triggerFileInput(item.id)}
                            title={hasFile ? 'Replace attached document' : 'Attach Document'}
                            disabled={fileUploads[item.id]?.uploading}
                          >
                            {fileUploads[item.id]?.uploading ? '...' : (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>)}
                            <span className="lg:hidden">{hasFile ? 'Replace' : 'Attach'}</span>
                          </button>
                        )}
                      </div>

                      {!isReadOnly && (
                        <button
                          type="button"
                          className="w-full min-h-[44px] lg:min-h-0 px-2 py-1 text-sm lg:text-xs rounded-lg lg:rounded border text-red-600 hover:bg-red-100 border-red-300 transition-colors flex items-center justify-center gap-1"
                          onClick={async () => {
                            if (await confirm('Are you sure you want to delete this expense item? This action cannot be undone.')) {
                              // A read still queued for this row is skipped; one
                              // already running finds no row and is dropped.
                              if (item.invoiceId) cancelledExtractionsRef.current.add(item.invoiceId);
                              deleteExpenseItem(item.id);
                            }
                          }}
                          title="Delete this row"
                          aria-label="Delete this line item"
                        >
Delete
                        </button>
                      )}

                      {fileUploads[item.id]?.uploading && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                          <span className="w-3 h-3 border-2 border-stanford-red border-t-transparent rounded-full animate-spin"></span>
                          Uploading…
                        </span>
                      )}
                      {hasFile && !rowInfected && !fileUploads[item.id]?.uploading && (
                        <span className="text-xs font-semibold text-amber-600">&#10003; Attached</span>
                      )}
                      {rowInfected && !fileUploads[item.id]?.uploading && (
                        <InfectedFileNotice audience={isAdminMode ? 'reviewer' : 'claimant'} />
                      )}

                      {/* Where invoices are read for this claim's owner: how
                          reading this row's document went. Only ever set in
                          the browser, so it is gone after a save and reload. */}
                      {item.extractionStatus && !fileUploads[item.id]?.uploading && (
                        <span role="status" aria-live="polite" className="text-xs leading-snug">
                          {item.extractionStatus === 'pending' && (
                            <span className="inline-flex items-center gap-1.5 text-gray-600">
                              <span className="w-3 h-3 border-2 border-stanford-red border-t-transparent rounded-full animate-spin flex-shrink-0"></span>
                              Reading invoice…
                            </span>
                          )}
                          {item.extractionStatus === 'done' && (
                            <span className="text-green-700">Filled from the invoice - please check</span>
                          )}
                          {item.extractionStatus === 'failed' && (
                            <span className="text-red-600">{"Couldn't read this document - please fill in manually"}</span>
                          )}
                        </span>
                      )}

                      <input
                        type="file"
                        id={`file-input-${item.id}`}
                        // MIME types as well as extensions, as in the bulk
                        // uploader, so phones offer Photos and the camera.
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileAttachment(item.id, e)}
                      />
                    </div>
                    );
                  })()}

                  {shouldHideActions && isInfected(item) && (
                    <div className="col-span-2 lg:col-span-1">
                      <InfectedFileNotice audience={isAdminMode ? 'reviewer' : 'claimant'} />
                    </div>
                  )}
                  {shouldHideActions && !isInfected(item) && (item.invoicePdfGoogleCloudUrl || item.pdfUrl) && (
                    <div className="col-span-2 lg:col-span-1">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          aria-label="View document"
                          className="min-h-[44px] lg:min-h-0 p-2 text-sm rounded-lg lg:rounded border text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors inline-flex items-center gap-1.5"
                          onClick={() => openPdfDetails(item.id)}
                          title="View document"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          <span className="lg:hidden">View document</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              {/* Admin Comments Display - for regular users to see admin feedback */}
              {!isAdminMode && item.adminComments && (claimStatus === 'returned' || claimStatus === 'rejected') && (
                <div className="bg-orange-50 border-b border-orange-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span>&#128172;</span>
                    <strong className="text-gray-800">Administrator Feedback:</strong>
                  </div>
                  <div className="text-gray-700">
                    {item.adminComments}
                  </div>
                </div>
              )}
              </div>
            ))}
          </div>
        </div>

        {/* Total Section */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <div className="flex items-baseline justify-between sm:justify-end w-full sm:w-auto gap-4">
            <span className="text-base sm:text-lg font-semibold text-gray-800">Total USD Amount:</span>
            <span className="text-xl sm:text-2xl font-bold text-stanford-red tabular-nums">
              ${liveTotalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Admins can take this claim into payroll as a spreadsheet, not just PDF. */}
        {/* Approval + rejection history - see the note in perdiemLodging.mjs.
            Expense claims additionally needed the backend claimType fix: the
            chain was queried as 'ExpenditureClaim' while every writer stored
            'ExpenseClaim', so this panel would have been permanently empty. */}
        {(claimId || currentClaimId) && (
          <div className="border-t border-gray-200 p-4">
            <ClaimHistoryPanel
              approvalChain={claimData?.approvalChain}
              editHistory={claimData?.editHistory}
              defaultOpen={claimStatus === 'returned' || claimStatus === 'rejected'}
            />
          </div>
        )}

        {(isAdminMode || isClaimReviewer) && !isCreateRoute && (claimId || currentClaimId) && (
          <div className="border-t border-gray-200 p-4">
            <button
              onClick={async () => {
                try {
                  await exportExpenseClaimToExcel(
                    { ...(claimData || {}), items: expenseItems },
                    ptaeAllocation
                  );
                } catch (err) {
                  alert('Could not export to Excel: ' + err.message);
                }
              }}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors"
            >
              Download Excel
            </button>
          </div>
        )}

        {/* PTAE allocation - the admin approves from this form, so funding must be
            assignable here (approval is blocked without it). */}
        {(isAdminMode || isClaimReviewer) && !isCreateRoute && (claimId || currentClaimId) && (
          <div className="border-t border-gray-200 p-4">
            <ClaimPtaeAllocation
              claimType="expense"
              claimId={claimId || currentClaimId}
              claimTotal={expenseItems?.length
                ? liveTotalUsd
                : (claimData?.totalUsdAmount ?? claimData?.totalAmount ?? 0)}
              // A reviewer never edits funding, whatever the claim's status.
              readOnly={!isAdminMode || !['submitted', 'returned', 'under_review'].includes(claimStatus)}
              onDirtyChange={setPtaeAllocationDirty}
              onAllocationLoaded={handleAllocationLoaded}
              liveLineValues={isAdminMode ? liveLineValues : null}
              // Reload once the claim is saved: the server re-prices the
              // receipts and resizes the buckets to match.
              refreshKey={saveSuccess}
              // Assign PTAE saves first when receipts were added or removed.
              onSaveClaim={isAdminMode && !isReadOnly ? saveAdminChanges : undefined}
            />
          </div>
        )}

        {/* The admin's Save and decision sit right under Assign PTAE (the user,
            2026-10-21): funding is the last thing set before approving, so the
            buttons are where the admin already is. The receipts stay below. */}
        {isAdminMode && !shouldHideActions && !isReadOnly && (
          <div className="border-t border-gray-200 p-4 flex flex-col-reverse sm:flex-row sm:flex-wrap gap-3">
            {/* order-last puts the error at the top of the reversed phone column. */}
            {error && <div className="w-full text-red-600 mb-2 order-last sm:order-none">{error}</div>}
            <button
              onClick={async () => {
                try {
                  await saveAdminChanges();
                } catch (_e) { /* error already set in hook */ }
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={isLoading || isSaving || ptaeAllocationDirty}
              title={ptaeAllocationDirty
                ? 'Save the PTA allocation first - saving the claim now would lose your allocation changes'
                : ''}
            >
              {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
            </button>
            <button
              onClick={async () => {
                const id = claimId || currentClaimId || claimData?.claimId;
                if (!id) return;
                if (await confirm('Discard your unsaved changes and restore this claim to its last-saved version?')) {
                  loadClaimData(id);
                }
              }}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              disabled={isLoading || isSaving}
            >
              Revert
            </button>
            <button
              onClick={handleAdminAction}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              disabled={isLoading || isSaving}
            >
              Take Administrative Action
            </button>
          </div>
        )}

        {/* No supporting documents on an expense claim (the user,
            2026-10-21): its evidence is the receipt on each line, shown below.
            Supporting documents stay on per diem claims only. */}

        {/* Form Actions - shown whenever the claim is editable (incl. returned
            claims the user needs to revise and re-submit). */}
        {!shouldHideActions && !isReadOnly && !isAdminMode && (
          <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row sm:flex-wrap gap-3">
            {error && <div className="w-full text-red-600 mb-2">{error}</div>}

            {!isAdminMode && (
              <>

                <button
                  onClick={async () => {
                    if (!await confirmIfStillReading('Save')) return;
                    saveExpenses(true, false);
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  disabled={isSaving}
                >
                  Save as Draft
                </button>

                {/* Only offer deletion once a draft actually exists, and only
                    while it is still a draft - a submitted claim must not be
                    removable from here. */}
                {(claimId || currentClaimId || claimData?.claimId) && claimStatus === 'draft' && (
                  <button
                    onClick={async () => {
                      const id = claimId || currentClaimId || claimData?.claimId;
                      if (!id) return;
                      if (!await confirm('Delete this draft claim? This cannot be undone.')) return;
                      try {
                        await deleteClaim(id);
                      } catch (_e) { /* error surfaced by the hook */ }
                    }}
                    className="px-6 py-3 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                    disabled={isSaving || isLoading}
                  >
                    Delete Draft
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (!await confirmIfStillReading('Submit')) return;
                    saveExpenses(false, false);
                  }}
                  className="order-first sm:order-none px-6 py-3 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
                  disabled={isSaving}
                >
                  {onBehalfName ? `File and submit for ${onBehalfName.split(' ')[0]}` : 'Submit Claim'}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Embedded PDFs */}
      <div key={`pdf-container-${pdfRenderKey}`}>
        {expenseItems
          .filter(item => {
            const hasOriginalPdf = item.invoicePdfGoogleCloudUrl || item.pdfUrl;
            const hasUploadedPdf = fileUploads[item.id]?.uploaded;
            return hasOriginalPdf || hasUploadedPdf;
          })
          .map((item, index) => {
            const actualItemId = fileUploads[item.id]?.backendItemId || item.id;
            const signedPdfUrl = item.pdfUrl || item.invoicePdfGoogleCloudUrl || null;

            return (
              <PDFRenderer
                key={`pdf-${item.id}-${actualItemId}-${pdfRenderKey}-${fileUploads[item.id]?.uploaded ? 'uploaded' : 'existing'}`}
                pdfUrl={signedPdfUrl}
                itemId={actualItemId}
                index={index}
                expenditureDescription={item.expenditureDescription}
                infected={isInfected(item)}
                noticeAudience={isAdminMode ? 'reviewer' : 'claimant'}
              />
            );
          })
        }
      </div>

      {/* City Search Dropdown Portal */}
      {!isReadOnly && portalElement && cityResults.length > 0 && activeSearchId && ReactDOM.createPortal(
        <div
          className="bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: dropdownPosition.width ? `${dropdownPosition.width}px` : '300px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 99999
          }}
        >
          <ul className="divide-y divide-gray-100">
            {cityResults.map(city => (
              <li
                key={city.id}
                onClick={() => handleCitySelection(activeSearchId, city.name, city.country)}
                className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-800">{city.name}</span>
                <span className="text-gray-500 ml-2">{city.country}</span>
              </li>
            ))}
          </ul>
        </div>,
        portalElement
      )}

      {/* PDF Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-0" onClick={closePdfModal}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full sm:mx-4 max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 p-3 sm:p-4 border-b border-gray-200 shrink-0">
              <h3 className="min-w-0 text-base sm:text-lg font-semibold text-gray-800">Document Viewer</h3>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => window.open(pdfUrl, '_blank')}
                  className="min-h-[44px] sm:min-h-0 whitespace-nowrap px-3 sm:px-4 py-2 text-sm text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors"
                  title="Open in new tab"
                >
                  Open in New Tab
                </button>
                <button className="min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 p-2 text-gray-400 hover:text-gray-600 transition-colors" onClick={closePdfModal} aria-label="Close document viewer">&#10005;</button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              {pdfUrl ? (
                <iframe
                  src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH&zoom=page-width`}
                  className="w-full h-[65dvh] sm:h-[70vh] border-0"
                  title="PDF Document Viewer"
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <p className="text-red-600 mb-4">{pdfError || "No PDF document is attached to this expense"}</p>
                  <button onClick={closePdfModal} className="px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors">
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Admin PDF Download Modal */}
      <AdminPdfDownloadModal
        isOpen={showAdminPdfModal}
        onClose={() => setShowAdminPdfModal(false)}
        expenseItems={expenseItems}
        API_URL={API_URL}
        getAxiosConfig={getAxiosConfig}
      />

      {/* Admin Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-0" onClick={() => setShowActionModal(false)}>
          {/* Scrolls inside the viewport: with the keyboard up (or the
              withdraw notice showing) the panel was taller than a phone. */}
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full sm:mx-4 p-4 sm:p-6 max-h-[calc(100dvh-1.5rem)] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Administrative Decision</h3>
              <button
                className="min-w-[44px] min-h-[44px] -mr-2 sm:min-w-0 sm:min-h-0 sm:mr-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowActionModal(false)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>

            <div className="space-y-4">
              {claimStatus !== 'approved' && (
              <div className="space-y-3">
                <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="adminAction"
                    value="approve"
                    checked={selectedAction === 'approve'}
                    onChange={(e) => setSelectedAction(e.target.value)}
                    className="mt-0.5 sm:mt-1 w-5 h-5 sm:w-auto sm:h-auto shrink-0"
                  />
                  <div>
                    <strong className="text-green-600">Approve Claim</strong>
                    <p className="text-sm text-gray-500">Claim meets all requirements</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="adminAction"
                    value="reject"
                    checked={selectedAction === 'reject'}
                    onChange={(e) => setSelectedAction(e.target.value)}
                    className="mt-0.5 sm:mt-1 w-5 h-5 sm:w-auto sm:h-auto shrink-0"
                  />
                  <div>
                    <strong className="text-red-600">Reject Claim</strong>
                    <p className="text-sm text-gray-500">Claim has issues that cannot be resolved</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="adminAction"
                    value="sendBack"
                    checked={selectedAction === 'sendBack'}
                    onChange={(e) => setSelectedAction(e.target.value)}
                    className="mt-0.5 sm:mt-1 w-5 h-5 sm:w-auto sm:h-auto shrink-0"
                  />
                  <div>
                    <strong className="text-orange-600">Send Back for Revision</strong>
                    <p className="text-sm text-gray-500">Needs changes before approval</p>
                  </div>
                </label>
              </div>
              )}

              {/* Approved is final: send-back is gone, and the server rejects
                  approve/reject on an already-approved claim. Say so rather than
                  offering options that come back as an error. */}
              {claimStatus === 'approved' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="font-semibold text-amber-800">Withdraw approval</p>
                  <p className="text-sm text-amber-700 mt-1">
                    This claim is approved and waiting for the auditor. Withdrawing returns it to
                    Under Review so you can correct it and approve again. Once the auditor has
                    audited it, or it has gone into a report under review, this is no longer possible.
                  </p>
                  <button
                    onClick={withdrawApproval}
                    disabled={isLoading}
                    className="mt-3 w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                  >
                    {isLoading ? 'Withdrawing…' : 'Withdraw approval'}
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comments {(selectedAction === 'reject' || selectedAction === 'sendBack') && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={adminDecisionComment}
                  onChange={(e) => setAdminDecisionComment(e.target.value)}
                  placeholder="Provide details about your decision..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                  rows="4"
                  required={selectedAction === 'reject' || selectedAction === 'sendBack'}
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 mt-4 sm:mt-6">
              <button
                onClick={() => setShowActionModal(false)}
                className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleModalSubmit}
                className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!selectedAction || isLoading || claimStatus === 'approved'}
              >
                Submit Decision
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-0" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full sm:mx-4 p-4 sm:p-6 max-h-[calc(100dvh-1.5rem)] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Delete Draft Claim</h3>
              <button
                className="min-w-[44px] min-h-[44px] -mr-2 sm:min-w-0 sm:min-h-0 sm:mr-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowDeleteModal(false)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-semibold text-red-800">Are you sure you want to delete this draft claim?</p>
                <p className="text-sm text-red-700 mt-1">This action cannot be undone. All expense items and attached documents will be permanently removed.</p>

                {expenseItems.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-red-800">This will delete {expenseItems.length} expense item(s):</p>
                    <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                      {expenseItems.slice(0, 3).map((item, index) => (
                        <li key={item.id}>
                          {item.expenditureDescription || `Expense item ${index + 1}`}
                          {item.receiptAmount && ` - $${item.receiptAmount}`}
                        </li>
                      ))}
                      {expenseItems.length > 3 && (
                        <li>... and {expenseItems.length - 3} more item(s)</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 mt-4 sm:mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await deleteClaim(claimId);
                    setShowDeleteModal(false);
                    // The hook should handle navigation after successful deletion
                  } catch (error) {
                    console.error('Error deleting claim:', error);
                    // Error handling should be in the hook
                  }
                }}
                className="flex-1 min-h-[44px] sm:min-h-0 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default ExpenseForm;
